import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { CurrencyProvider } from './context/CurrencyContext'
import Login from './pages/Login'
import ResetPassword from './pages/ResetPassword'
import Home from './pages/Home'
import DealerCatalog from './pages/DealerCatalog'
import WatchDetail from './pages/WatchDetail'
import AgentListings from './pages/AgentListings'
import AgentProfile from './pages/AgentProfile'
import AdminPanel from './pages/AdminPanel'
import DealerOffers from './pages/DealerOffers'
import MyAccount from './pages/MyAccount'
import JoinPage from './pages/JoinPage'
import SupplierDashboard from './pages/SupplierDashboard'
import JewellerySupplierDashboard from './pages/JewellerySupplierDashboard'
import PreviewCatalog from './pages/PreviewCatalog'
import PreviewDetail from './pages/PreviewDetail'
import WantToBuyPage from './pages/WantToBuyPage'

function authErrorFromHash() {
  // Supabase puts auth errors in the URL hash, e.g.:
  // /#error=access_denied&error_code=otp_expired&...
  const hash = window.location.hash
  if (!hash || !hash.includes('error=')) return null
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  return params.get('error_code') || params.get('error') || 'unknown'
}

function PrivateRoute({ children, allowedRoles }) {
  const { user, profile, loading } = useAuth()
  if (loading) return <div className="loading-page"><div className="spinner" /></div>
  if (!user) {
    const errCode = authErrorFromHash()
    if (errCode) return <Navigate to={`/login?auth_error=${encodeURIComponent(errCode)}`} replace />
    return <Navigate to="/login" replace />
  }
  if (allowedRoles && !allowedRoles.includes(profile?.role) && !(profile?.is_supplier && allowedRoles.includes('supplier'))) return <Navigate to="/" replace />
  return children
}

function RoleRedirect() {
  const { profile, loading } = useAuth()
  if (loading) return <div className="loading-page"><div className="spinner" /></div>
  if (!profile) return <Navigate to="/login" replace />
  if (profile.role === 'admin') return <Navigate to="/admin" replace />
  if (profile.role === 'agent') return <Navigate to="/home" replace />
  if (profile.role === 'b2c') return <Navigate to="/home" replace />
  if (profile.role === 'supplier') return <Navigate to="/supplier" replace />
  if (profile.role === 'jewellery_supplier') return <Navigate to="/jewellery-supplier" replace />
  if (profile.role === 'jewellery_agent') return <Navigate to="/agent" replace />
  return <Navigate to="/home" replace />
}

export default function App() {
  return (
    <AuthProvider>
      <CurrencyProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/preview/:token" element={<PreviewCatalog />} />
          <Route path="/preview/:token/detail" element={<PreviewDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/join/:token" element={<JoinPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<PrivateRoute><RoleRedirect /></PrivateRoute>} />
          <Route path="/home" element={<PrivateRoute allowedRoles={['dealer', 'agent', 'admin', 'b2c', 'jewellery_agent']}><Home /></PrivateRoute>} />
          <Route path="/catalog" element={<PrivateRoute allowedRoles={['dealer', 'admin', 'agent', 'b2c', 'jewellery_agent']}><DealerCatalog /></PrivateRoute>} />
          <Route path="/watches" element={<PrivateRoute allowedRoles={['dealer', 'admin', 'agent', 'b2c', 'jewellery_agent']}><DealerCatalog routeCategory="Watches" /></PrivateRoute>} />
          <Route path="/jewellery" element={<PrivateRoute allowedRoles={['dealer', 'admin', 'agent', 'b2c', 'jewellery_agent']}><DealerCatalog routeCategory="Jewellery" /></PrivateRoute>} />
          <Route path="/bags" element={<PrivateRoute allowedRoles={['dealer', 'admin', 'agent', 'b2c', 'jewellery_agent']}><DealerCatalog routeCategory="Bags" /></PrivateRoute>} />
          <Route path="/catalog/:slug" element={<PrivateRoute><WatchDetail /></PrivateRoute>} />
          <Route path="/agent" element={<PrivateRoute allowedRoles={['agent', 'admin', 'jewellery_agent']}><AgentListings /></PrivateRoute>} />
          <Route path="/agent/:agentId" element={<PrivateRoute allowedRoles={['agent', 'admin']}><AgentProfile /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute allowedRoles={['admin']}><AdminPanel /></PrivateRoute>} />
          <Route path="/offers" element={<PrivateRoute allowedRoles={['dealer']}><DealerOffers /></PrivateRoute>} />
          <Route path="/account" element={<PrivateRoute><MyAccount /></PrivateRoute>} />
          <Route path="/supplier" element={<PrivateRoute allowedRoles={['supplier']}><SupplierDashboard /></PrivateRoute>} />
          <Route path="/jewellery-supplier" element={<PrivateRoute allowedRoles={['jewellery_supplier']}><JewellerySupplierDashboard /></PrivateRoute>} />
          <Route path="/want-to-buy" element={<PrivateRoute allowedRoles={['admin', 'agent', 'jewellery_agent']}><WantToBuyPage /></PrivateRoute>} />
        </Routes>
      </BrowserRouter>
      </CurrencyProvider>
    </AuthProvider>
  )
}
